import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from "ui/components/chart";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "ui/components/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/components/select";
import { useBilledVsCollectedChart } from "../hooks/useBilledVsCollectedChart";
import { useQuarterlyPerformance } from "../hooks/useQuarterlyPerformance";
import { DisplayAmount } from "components/display/amount";
import { formatAmount, formatDateForAPI } from "utils/utils";
import ChartLoader from "@/components/loader/chart";
import { generateYAxisTicks } from "@/lib/utils";

import { EvtStatsCard } from "components/stats/display-card";
import { EvtStatsCardLoader } from "components/stats/loader";

const chartConfig = {
  contracted: {
    label: "Contracted",
    color: "#fda4af",
  },
  billed: {
    label: "Billed",
    color: "#fb7185",
  },
  collected: {
    label: "Collected",
    color: "#f43f5e",
  },
};

const getCurrentYear = () => {
  const now = new Date();
  const month = now.getMonth() + 1;
  const year = now.getFullYear();
  return month <= 3 ? year - 1 : year;
};

const getYearOptions = () => {
  const currentYear = getCurrentYear();
  const years = [];
  for (let i = 0; i < 3; i++) {
    years.push(currentYear - i);
  }
  return years;
};

const groupDataByQuarters = (monthlyData) => {
  if (!monthlyData || !Array.isArray(monthlyData)) return [];

  const quarters = {};

  monthlyData.forEach((item) => {
    const [monthName, year] = item.month.split(" ");
    const monthIndex = new Date(`${monthName} 1, ${year}`).getMonth() + 1;

    let quarter, fiscalYear;
    if (monthIndex >= 4 && monthIndex <= 6) {
      quarter = 1;
      fiscalYear = parseInt(year);
    } else if (monthIndex >= 7 && monthIndex <= 9) {
      quarter = 2;
      fiscalYear = parseInt(year);
    } else if (monthIndex >= 10 && monthIndex <= 12) {
      quarter = 3;
      fiscalYear = parseInt(year);
    } else {
      quarter = 4;
      fiscalYear = parseInt(year) - 1;
    }

    const fyStart = fiscalYear;
    const fyEndShort = (fiscalYear + 1).toString().slice(-2);
    const quarterKey = `Q${quarter} FY ${fyStart}-${fyEndShort}`;

    if (!quarters[quarterKey]) {
      quarters[quarterKey] = {
        quarter: quarterKey,
        sortYear: fyStart,
        sortQuarter: quarter,
        months: [],
      };
    }

    quarters[quarterKey].months.push({
      monthName: monthName,
      ...item,
    });
  });

  // Convert to array and sort by fiscal year and quarter
  return Object.values(quarters).sort((a, b) => {
    if (a.sortYear !== b.sortYear) return a.sortYear - b.sortYear;
    return a.sortQuarter - b.sortQuarter;
  });
};

// Transform data for quarterly chart display
const transformDataForChart = (monthlyData) => {
  const quarterlyGroups = groupDataByQuarters(monthlyData);

  return quarterlyGroups.map((quarterGroup) => {
    const quarterData = {
      quarter: quarterGroup.quarter,
    };

    // Add months as month1, month2, month3 for each metric
    quarterGroup.months.forEach((month, index) => {
      const monthKey = `month${index + 1}`;
      quarterData[`${monthKey}Contracted`] = month.contracted;
      quarterData[`${monthKey}Billed`] = month.billed;
      quarterData[`${monthKey}Collected`] = month.collected;
      quarterData[`${monthKey}Name`] = month.monthName; // Store month name for tooltip
    });

    return quarterData;
  });
};

export function BilledVsCollectedChart() {
  const [selectedYear, setSelectedYear] = useState(getCurrentYear().toString());

  const handleYearChange = (year) => {
    setSelectedYear(year);
  };

  // Convert selected year to financial year date range
  const getFinancialYearRange = (year) => {
    const financialYear = parseInt(year);
    const startDate = new Date(financialYear, 3, 1); // April 1st
    const endDate = new Date(financialYear + 1, 2, 31); // March 31st next year

    return {
      from: formatDateForAPI(startDate),
      to: formatDateForAPI(endDate),
    };
  };

  const dateRange = getFinancialYearRange(selectedYear);

  const { data, isLoading, error } = useBilledVsCollectedChart({
    dateRange: dateRange,
  });

  const {
    data: performanceData,
    isLoading: performanceLoading,
    error: performanceError,
  } = useQuarterlyPerformance({
    year: parseInt(selectedYear),
  });

  // Transform data for quarterly display
  const chartData = transformDataForChart(data);

  if (isLoading) {
    return <ChartLoader />;
  }

  if (error) {
    return (
      <Card className="mx-6 mt-4">
        <CardContent className="pt-6">
          <div className="text-center py-8 text-destructive">
            Error loading billed vs collected data: {error.message}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate max value from all monthly data points
  const allValues = chartData.flatMap((quarter) =>
    ["month1", "month2", "month3"]
      .flatMap((monthKey) => [
        quarter[`${monthKey}Contracted`],
        quarter[`${monthKey}Billed`],
        quarter[`${monthKey}Collected`],
      ])
      .filter((value) => value && value > 0)
  );
  const maxValue = allValues.length > 0 ? Math.max(...allValues) : 0;
  const yAxisMax = Math.ceil(maxValue * 1.1);

  const yAxisTicks = generateYAxisTicks(yAxisMax);

  const getQuarterSums = (quarter) => {
    if (!quarter) return { contracted: 0, billed: 0, collected: 0 };
    const months = ["month1", "month2", "month3"];
    const sumFor = (metric) =>
      months.reduce((acc, m) => acc + (quarter[`${m}${metric}`] || 0), 0);
    return {
      contracted: sumFor("Contracted"),
      billed: sumFor("Billed"),
      collected: sumFor("Collected"),
    };
  };

  return (
    <Card className="mx-6 mt-4 border-none">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>
              Quarterly Growth: Contracted vs Billed vs Collected
            </CardTitle>
            <CardDescription>
              Quarterly breakdown with monthly details for FY {selectedYear}-
              {String(parseInt(selectedYear) + 1).slice(-2)}
            </CardDescription>
          </div>
          <div className="flex items-center gap-4">
            <Select value={selectedYear} onValueChange={handleYearChange}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Select year" />
              </SelectTrigger>
              <SelectContent>
                {getYearOptions().map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {`FY ${year}-${String(year + 1).slice(-2)}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4 mb-6">
          {performanceLoading
            ? [...Array(4)].map((_, i) => <EvtStatsCardLoader key={i} />)
            : performanceData?.map((item) => {
                const q = chartData.find((q) =>
                  q.quarter.startsWith(`Q${item.quarter} `)
                );
                const sums = getQuarterSums(q);
                return (
                  <EvtStatsCard
                    key={item.quarter}
                    label={`Q${item.quarter}`}
                    value={
                      <span className="font-semibold">
                        <DisplayAmount value={sums.contracted} format={true} />
                        {" / "}
                        <DisplayAmount value={sums.billed} format={true} />
                        {" / "}
                        <DisplayAmount value={sums.collected} format={true} />
                      </span>
                    }
                    difference={item.differenceFromPreviousQuarter}
                  />
                );
              })}
        </div>

        {!chartData || chartData.length === 0 ? (
          <div className="flex h-[400px] items-center justify-center text-muted-foreground">
            No data available for FY {selectedYear}-
            {String(parseInt(selectedYear) + 1).slice(-2)}
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="h-[400px] w-full">
            <BarChart
              accessibilityLayer
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              barCategoryGap="15%"
              barGap={2}
            >
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="quarter"
                tickLine={false}
                tickMargin={10}
                axisLine={false}
                fontSize={12}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                fontSize={12}
                tickFormatter={formatAmount}
                domain={[0, yAxisMax]}
                ticks={yAxisTicks}
              />

              <ChartTooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;

                  const quarterData = payload[0].payload;

                  // Group payload by month
                  const monthGroups = {};
                  payload.forEach((entry) => {
                    const monthMatch = entry.dataKey.match(/month(\d+)(\w+)/);
                    if (monthMatch) {
                      const monthIndex = parseInt(monthMatch[1]);
                      const metric = monthMatch[2];
                      const monthName =
                        quarterData[`month${monthIndex}Name`] ||
                        `Month ${monthIndex}`;

                      if (!monthGroups[monthName]) {
                        monthGroups[monthName] = {};
                      }
                      monthGroups[monthName][metric.toLowerCase()] =
                        entry.value;
                    }
                  });

                  return (
                    <div className="bg-background border border-border rounded-lg shadow-lg p-3">
                      <div className="font-semibold text-foreground mb-2">
                        {quarterData.quarter}
                      </div>
                      {Object.entries(monthGroups).map(
                        ([monthName, metrics]) => (
                          <div key={monthName} className="mb-3 last:mb-0">
                            <div className="font-medium  text-muted-foreground mb-1">
                              {monthName}
                            </div>
                            <div className="space-y-1">
                              {Object.entries(metrics).map(
                                ([metric, value]) => (
                                  <div
                                    key={metric}
                                    className="flex items-center justify-between gap-4"
                                  >
                                    <div className="flex items-center gap-2">
                                      <div
                                        className="w-2 h-2 rounded-sm"
                                        style={{
                                          backgroundColor: `var(--color-${metric})`,
                                        }}
                                      />
                                      <span className="">
                                        {chartConfig[metric]?.label || metric}
                                      </span>
                                    </div>
                                    <DisplayAmount
                                      value={value}
                                      format={true}
                                    />
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  );
                }}
              />
              <ChartLegend
                content={() => (
                  <div className="flex justify-center gap-6 mt-4">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 "
                        style={{ backgroundColor: "var(--color-contracted)" }}
                      />
                      <span className="font-medium">Contracted</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 "
                        style={{ backgroundColor: "var(--color-billed)" }}
                      />
                      <span className="font-medium">Billed</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 "
                        style={{ backgroundColor: "var(--color-collected)" }}
                      />
                      <span className="font-medium">Collected</span>
                    </div>
                  </div>
                )}
              />

              {/* Month 1 bars */}
              <Bar
                dataKey="month1Contracted"
                fill="var(--color-contracted)"
                radius={[4, 4, 0, 0]}
                legendType="none"
              />
              <Bar
                dataKey="month1Billed"
                fill="var(--color-billed)"
                radius={[4, 4, 0, 0]}
                legendType="none"
              />
              <Bar
                dataKey="month1Collected"
                fill="var(--color-collected)"
                radius={[4, 4, 0, 0]}
                legendType="none"
              />

              {/* Month 2 bars */}
              <Bar
                dataKey="month2Contracted"
                fill="var(--color-contracted)"
                radius={[4, 4, 0, 0]}
                legendType="none"
              />
              <Bar
                dataKey="month2Billed"
                fill="var(--color-billed)"
                radius={[4, 4, 0, 0]}
                legendType="none"
              />
              <Bar
                dataKey="month2Collected"
                fill="var(--color-collected)"
                radius={[4, 4, 0, 0]}
                legendType="none"
              />

              {/* Month 3 bars */}
              <Bar
                dataKey="month3Contracted"
                fill="var(--color-contracted)"
                radius={[4, 4, 0, 0]}
                legendType="none"
              />
              <Bar
                dataKey="month3Billed"
                fill="var(--color-billed)"
                radius={[4, 4, 0, 0]}
                legendType="none"
              />
              <Bar
                dataKey="month3Collected"
                fill="var(--color-collected)"
                radius={[4, 4, 0, 0]}
                legendType="none"
              />
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
